import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { applyMultipleGutscheine } from "@/lib/gutschein";
import { normalizeBookingResult, parseErrorResponse } from "@/lib/normalizeBookingResult";
import BookingResultView, { type BookingResultResponse } from "@/components/BookingResultView";

const WEBHOOK_URL = "https://n8n.cogniiq.co/webhook/paypal-return";

function getOrderID(): string | null {
  const p = new URLSearchParams(window.location.search);
  return p.get("token") || p.get("orderID");
}

function dedupKey(orderID: string) {
  return `paypal_booking_processed_${orderID}`;
}

type UIState = "loading" | "result" | "no-token";

export default function PaypalReturn() {
  const [uiState, setUiState] = useState<UIState>("loading");
  const [result, setResult] = useState<BookingResultResponse | null>(null);

  useEffect(() => {
    (async () => {
      const orderID = getOrderID();

      if (!orderID) {
        setUiState("no-token");
        return;
      }

      // Already processed in this session → show cached success
      if (sessionStorage.getItem(dedupKey(orderID))) {
        setResult({ success: true, bookingConfirmed: true });
        setUiState("result");
        return;
      }

      let res: Response | undefined;
      try {
        res = await fetch(WEBHOOK_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderID }),
        });
      } catch (networkErr) {
        // True network failure (CORS, offline, DNS) — unknown state
        console.error("[PaypalReturn] network error:", networkErr);
        setResult(normalizeBookingResult(null, { paymentCharged: true }));
        setUiState("result");
        return;
      }

      // Try to parse the body regardless of status
      let json: Partial<BookingResultResponse> | null = null;
      try {
        const ct = res.headers.get("content-type") ?? "";
        if (ct.includes("application/json")) {
          json = await res.json();
        } else {
          json = await parseErrorResponse(res);
        }
      } catch {
        json = null;
      }
      console.info("[PaypalReturn] status:", res.status, "body:", json);

      // Any explicit failure from n8n (including 409 double-booking conflicts)
      if (json?.success === false || res.status === 409) {
        setResult({
          success: false,
          bookingConfirmed: false,
          paymentRefunded: json?.paymentRefunded ?? true,
          title: json?.title || "Die Buchung konnte nicht abgeschlossen werden",
          message: json?.message || "Falls eine Zahlung erfolgt ist, prüfen wir sie automatisch.",
          refundMessage: json?.refundMessage,
          nextStep: json?.nextStep,
          contact: json?.contact,
        });
        setUiState("result");
        return;
      }

      // Non-2xx with no parseable failure body → unknown state
      if (!res.ok) {
        console.error("[PaypalReturn] non-2xx without failure body:", res.status);
        setResult(normalizeBookingResult(null, { paymentCharged: true }));
        setUiState("result");
        return;
      }

      // Success path — apply pending gutscheine
      const pendingData = sessionStorage.getItem("pendingGutscheine");
      if (pendingData) {
        try {
          const { gutscheine, subtotal, customerEmail } = JSON.parse(pendingData);
          await applyMultipleGutscheine(gutscheine, subtotal, customerEmail);
          sessionStorage.removeItem("pendingGutscheine");
        } catch {
          // non-fatal
        }
      }

      sessionStorage.setItem(dedupKey(orderID), "1");

      // n8n returns { success: true, booking_id, message } — treat as confirmed
      setResult({
        success: true,
        bookingConfirmed: true,
        title: "Buchung erfolgreich",
        message: json?.message || "Vielen Dank. Deine Buchung wurde bestätigt.",
      });
      setUiState("result");
    })();
  }, []);

  if (uiState === "loading") {
    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[560px]"
        >
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex flex-col items-center justify-center py-6 gap-5 text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--svh-crest-wash)' }}
                >
                  <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--svh-crest)' }} strokeWidth={2} />
                </div>
                <div>
                  <p className="svh-h3 mb-1.5">Buchung wird abgeschlossen …</p>
                  <p className="svh-meta">Einen Moment bitte. Bitte schließen Sie dieses Fenster nicht.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </motion.div>
      </div>
    );
  }

  if (uiState === "no-token") {
    return (
      <BookingResultView
        result={{
          success: false,
          bookingConfirmed: false,
          paymentRefunded: false,
          title: "Buchung konnte nicht abgeschlossen werden",
          message: "Es wurde keine gültige Zahlungsreferenz gefunden. Bitte kontaktiere den Verein.",
        }}
        autoRedirect={false}
      />
    );
  }

  return (
    <BookingResultView
      result={result}
      autoRedirect={result?.success === true && result?.bookingConfirmed !== false}
      redirectUrl="https://svheinersreuth-padel.de/"
    />
  );
}
